import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import {
  ReplyBlockedRelayDto,
  ReplyDeletedRelayDto,
  ReplyRelayDto,
} from '@diritto/common/relay/dtos/episode-reply-relay.dto';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { ellipsis } from '@diritto/common/utils/string';
import styled from '@emotion/styled';
import { ProfileImage } from 'app-components/accounts/profiles/ProfileImage';
import { useAuthState } from 'contexts/AuthProvider';
import { ArrowRightSlimIcon } from 'icons/ArrowRightSlimIcon';
import { DotDividerIcon } from 'icons/DotDividerIcon';
import { InfoFilledIcon } from 'icons/InfoFilledIcon';
import { ReplyIcon } from 'icons/ReplyIcon';
import { useRouter } from 'next/navigation';
import React, { memo, useCallback, useMemo } from 'react';
import { Divider } from 'ui-components/common/Divider';
import { IconButton } from 'ui-components/common/IconButton';
import { Link } from 'ui-components/common/Link';
import { useRequestCertificate } from 'ui-hooks/useRequestCertificate';
import { useRequestSign } from 'ui-hooks/useRequestSign';
import { showAlertModal } from 'utils/modal';

interface DeletedReplyProps {
  className?: string;
  reply: ReplyDeletedRelayDto | ReplyBlockedRelayDto;
  content?: ContentPublicRelayDto;
  episode?: EpisodePublicRelayDto;
  authorProfile?: ProfileLitePublicRelayDto;
  showReReplyCount?: boolean;
  onClickReReply?: (reply: ReplyRelayDto) => void;
}

export const BlockedOrDeletedReply = memo(
  ({
    className,
    reply,
    content,
    episode,
    authorProfile,
    onClickReReply,
    showReReplyCount = false,
  }: DeletedReplyProps) => {
    const router = useRouter();
    const { isLoggedIn, user } = useAuthState();
    const requestSign = useRequestSign();
    const requestCertificate = useRequestCertificate();
    const isShowReReply = showReReplyCount;

    const handleClickReReply = useCallback(
      (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();

        if (reply.counts.reReplyCount === 0) {
          if (!isLoggedIn) {
            requestSign();
            return;
          }

          if (!user?.ageVerified) {
            showAlertModal(
              { title: '본인인증을 해주세요.', description: '해당 기능을 사용하려면 본인인증이 필요해요.' },
              {
                onConfirm: requestCertificate,
                confirmText: '본인인증하러 가기',
                cancelText: '닫기',
              },
            );
            return;
          }
        }

        if (onClickReReply) {
          onClickReReply?.(reply);
          return;
        }

        const reReplyUrl = `/contents/${reply.contentId}/episodes/${reply.episodeId}/replies/${reply.id}?isInputFocus=true`;
        router.push(reReplyUrl);
      },
      [reply, onClickReReply, router, isLoggedIn, user?.ageVerified, requestSign, requestCertificate],
    );

    const description = useMemo(
      () => (reply.blockedType ? '신고되어 숨겨진 글이에요.' : '삭제된 댓글이에요.'),
      [reply.blockedType],
    );

    if (!isShowReReply && !reply.counts.reReplyCount) {
      return null;
    }

    return (
      <StyledContainer className={className}>
        <StyledDescriptionArea>
          <StyledIconWrapper>
            <InfoFilledIcon className="size-[16px]" color={Color.Gray500} />
          </StyledIconWrapper>
          <StyledText>{description}</StyledText>
        </StyledDescriptionArea>
        {(content || episode) && (
          <StyledLinkWrapper>
            {content && (
              <>
                <Link href={`/contents/${content.id}`}>
                  <StyledEpisodeTitle>{ellipsis(content.title, 15)}</StyledEpisodeTitle>
                </Link>
                <ArrowRightSlimIcon size={12} color={Color.Gray300} />
              </>
            )}
            {episode && (
              <Link href={`/contents/${episode.contentId}/episodes/${episode.id}`}>
                <StyledEpisodeTitle>{episode.title}</StyledEpisodeTitle>
              </Link>
            )}
          </StyledLinkWrapper>
        )}
        {isShowReReply && (
          <StyledReplyInfo>
            <IconButton onClick={handleClickReReply}>
              <StyledIconWrapper>
                <ReplyIcon color={Color.Gray500} className="size-[24px]" />
              </StyledIconWrapper>
              <StyledFavoriteText>{reply.counts.reReplyCount}</StyledFavoriteText>
              {reply.isAuthorReplied && authorProfile && (
                <StyledAuthorReactionArea>
                  <DotDividerIcon color={Color.Gray300} size={3} />
                  <ProfileImage file={authorProfile.profileImage} defaultImageKey={authorProfile.nickname} size={18} />
                </StyledAuthorReactionArea>
              )}
            </IconButton>
          </StyledReplyInfo>
        )}
        <StyledDivider position="bottom" color={Color.Gray200} />
      </StyledContainer>
    );
  },
);

BlockedOrDeletedReply.displayName = 'BlockedOrDeletedReply';

const StyledLinkWrapper = styled.div`
  display: flex;
  margin-top: 4px;
  margin-left: 48px;
  flex-direction: row;
  align-items: center;
  height: 16px;
`;

const StyledContainer = styled.div`
  display: flex;
  background-color: ${Color.White};
  position: relative;
  padding: 16px;
  width: 100%;
  flex-direction: column;
`;

const StyledDivider = styled(Divider)`
  width: calc(100% - 32px);
  left: 16px;
`;

const StyledDescriptionArea = styled.div`
  display: flex;
  align-items: center;
`;

const StyledEpisodeTitle = styled.p`
  font-weight: ${FontWeight.Medium};
  color: ${Color.Gray500};
  ${Typography.XXXSmall};
  word-break: break-all;
  white-space: break-spaces;
`;

const StyledIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 16px;
  height: 16px;
  margin-right: 4px;
`;

const StyledText = styled.div`
  ${Typography.XXSmall};
  color: ${Color.Gray900};
`;

const StyledFavoriteText = styled.p`
  color: ${Color.Gray500};
  ${Typography.XXXSmall};
`;

const StyledReplyInfo = styled.div`
  margin-left: 48px;
  margin-top: 16px;
  position: relative;
  flex-grow: 1;
`;

const StyledAuthorReactionArea = styled.div`
  display: flex;
  align-items: center;

  & > * {
    margin-left: 4px;
  }
`;
